// Settings changes on a simulated league (LEAGUE_SETTINGS.md): validate,
// version, apply from the right period, and preview a candidate's scoring
// on the current period's real stat lines with the same engine.

import {
  diffSettings,
  validateLeagueSettings,
  type Issue,
  type LeagueSettings,
  type LeagueSettingsVersion,
  type TeamRef,
} from '@fsp/domain';
import { scorePlayer } from '@fsp/scoring';
import { football } from '@fsp/sports-football';
import { gameFor, type SimState } from './league';
import { gameProgress, lineAt } from './games';
import { teamRef } from './views';

const slotDefs = new Map(football.slots.map((s) => [s.id, s]));

export type SaveResult =
  { ok: true; version: LeagueSettingsVersion } | { ok: false; issues: Issue[] };

/**
 * Save a new settings version. Errors from the validator refuse the save;
 * warnings ride along. After the draft a reason is required and the change
 * applies from the next period (governance.scoringChangesApply).
 */
export function saveSettings(
  state: SimState,
  candidate: LeagueSettings,
  opts: { reason: string; by: string; unlock?: boolean; now: number },
): SaveResult {
  const issues = validateLeagueSettings(candidate, football, state.settings);
  const drafted = state.status === 'in-season';
  if (drafted && !opts.reason.trim())
    issues.push({
      path: 'reason',
      severity: 'error',
      message: 'A reason is required once the league has drafted.',
    });
  const lockedTouched = drafted
    ? state.settings.governance.lockedAfterDraft.filter(
        (section) =>
          JSON.stringify((state.settings as Record<string, unknown>)[section]) !==
          JSON.stringify((candidate as Record<string, unknown>)[section]),
      )
    : [];
  if (lockedTouched.length && !opts.unlock)
    issues.push({
      path: `governance.lockedAfterDraft`,
      severity: 'error',
      message: `${lockedTouched.join(', ')} ${lockedTouched.length === 1 ? 'is' : 'are'} locked after the draft. Unlock to change ${lockedTouched.length === 1 ? 'it' : 'them'}.`,
    });
  if (issues.some((i) => i.severity === 'error')) return { ok: false, issues };

  const diff = diffSettings(state.settings, candidate);
  const last = state.settingsVersions[state.settingsVersions.length - 1];
  const version: LeagueSettingsVersion = {
    leagueId: state.leagueId,
    version: (last?.version ?? 0) + 1,
    settings: candidate,
    createdAt: new Date(opts.now).toISOString(),
    createdBy: opts.by,
    reason: opts.unlock
      ? `[unlocked ${lockedTouched.join(', ')}] ${opts.reason}`.trim()
      : opts.reason,
    diff,
    effectiveFromPeriod: !drafted
      ? 1
      : candidate.governance.scoringChangesApply === 'whole-season'
        ? 1
        : state.currentPeriod + 1,
    approvedBy: [],
  };
  state.settingsVersions.push(version);
  state.settings = candidate;
  state.scoring = candidate.scoring;
  return { ok: true, version };
}

export interface PreviewRow {
  team: TeamRef;
  current: number;
  candidate: number;
  delta: number;
  rankNow: number;
  rankThen: number;
}

export interface SettingsPreview {
  period: number;
  /** 'pre' means no stat lines yet, so nothing to preview. */
  status: 'pre' | 'live' | 'final';
  rows: PreviewRow[];
}

/**
 * Score every team's current starters under the candidate scoring and under
 * the current scoring, from the same stat lines. The engine is the one the
 * matchup screen uses; the preview is just two calls of it.
 */
export function previewSettings(
  state: SimState,
  candidate: LeagueSettings,
  now: number,
): SettingsPreview {
  const starts = state.proGames.map((g) => g.startAt);
  const status: SettingsPreview['status'] =
    starts.length === 0 || now < Math.min(...starts)
      ? 'pre'
      : now >= Math.max(...state.proGames.map((g) => g.startAt + 11700 * 1000))
        ? 'final'
        : 'live';
  const rows: PreviewRow[] = [];
  if (status === 'pre' || state.status !== 'in-season')
    return { period: state.currentPeriod, status: 'pre', rows };

  for (const team of state.teams) {
    let current = 0;
    let cand = 0;
    for (const row of team.lineup) {
      if (!row.playerId || slotDefs.get(row.slot)?.kind !== 'starter') continue;
      const p = state.players.get(row.playerId)!;
      const g = gameFor(state, p);
      if (!g || now < g.startAt) continue;
      const line = lineAt(state.events.get(p.id) ?? [], gameProgress(g, now));
      const games = [{ gameId: g.id, line }];
      current += scorePlayer(games, p.positions, state.scoring).total;
      cand += scorePlayer(games, p.positions, candidate.scoring).total;
    }
    rows.push({
      team: teamRef(state, team, now),
      current: Math.round(current * 10) / 10,
      candidate: Math.round(cand * 10) / 10,
      delta: Math.round((cand - current) * 10) / 10,
      rankNow: 0,
      rankThen: 0,
    });
  }
  const byCurrent = [...rows].sort((a, b) => b.current - a.current);
  const byCandidate = [...rows].sort((a, b) => b.candidate - a.candidate);
  for (const r of rows) {
    r.rankNow = byCurrent.indexOf(r) + 1;
    r.rankThen = byCandidate.indexOf(r) + 1;
  }
  rows.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || a.rankNow - b.rankNow);
  return { period: state.currentPeriod, status, rows };
}
